import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined'
import SoundFilled from '@ant-design/icons/SoundFilled'
import SoundOutlined from '@ant-design/icons/SoundOutlined'
import { Select } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { useZoomToAnnotation } from 'features/kestrelViewer/hooks/useZoomToAnnotation'
import {
  recordDataValidationEnd,
  recordDataValidationListExhausted,
  recordDataValidationStart,
} from 'features/kestrelViewer/metrics'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { SeverityPicker } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/SeverityPicker'
import { useDeleteSound } from 'features/kestrelViewer/ui/tools/dataValidation/components/useDeleteSound'
import { useSuccessSound } from 'features/kestrelViewer/ui/tools/dataValidation/components/useSuccessSound'
import { DataValidationPanelContent } from 'features/kestrelViewer/ui/tools/dataValidation/DataValidationPanelContent'
import { useAnnotationAutoSave } from 'features/kestrelViewer/ui/tools/dataValidation/hooks/useNotesAutoSave'
import {
  formatArea,
  formatPerimeter,
} from 'features/kestrelViewer/utils/unitConversionUtils'
import { useGeckoLocale } from 'features/locale'
import { CheckCircle } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { KVAnnotation, RolodexEntryPatchJson, RooftopDamageId } from 'types'

const slideInFromBottom = keyframes`
  from {
    opacity: 0;
    transform: translateY(2rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideOutToBottom = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(2rem);
  }
`

const PanelContainer = styled.div<{ $isClosing: boolean }>`
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 1.5rem;
  bottom: 1.1rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.2rem 0.5rem 0.5rem 0.5rem;
  width: 50rem;
  height: 195px;
  z-index: 2001;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${({ $isClosing }) =>
    $isClosing
      ? css`
          ${slideOutToBottom} 0.25s ease-in forwards
        `
      : css`
          ${slideInFromBottom} 0.3s ease-out forwards
        `};
`

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 2rem;
`

const PanelHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`

const PanelHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const PanelHeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const FilterDropdown = styled(Select)`
  width: 12rem;

  .ant-select-selector {
    background: rgba(0, 0, 0, 0.4) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 0.25rem !important;
    color: white !important;
    font-size: 0.75rem !important;
    height: 1.5rem !important;
    padding: 0 0.5rem !important;
  }

  .ant-select-selection-item {
    color: white !important;
    line-height: 1.5rem !important;
  }

  .ant-select-arrow {
    color: rgba(255, 255, 255, 0.6) !important;
  }

  &:hover .ant-select-selector {
    border-color: rgba(255, 255, 255, 0.4) !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: rgba(255, 255, 255, 0.5) !important;
    box-shadow: none !important;
  }
`

const PanelTitle = styled.div`
  color: white;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.05rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

const AnnotationCounter = styled.span`
  font-size: 0.75rem;
  opacity: 0.7;
  font-weight: 400;
  min-width: 4rem;
`

const AnnotationLabelInput = styled.input`
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  min-width: 6rem;
  max-width: 14rem;
  flex-shrink: 1;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  outline: none;
  transition: all 0.15s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    background: rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
  }
`

const AnnotationLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 1;
  min-width: 0;
`

const ValidatedIcon = styled(CheckCircle)`
  color: #4caf50;
  width: 0.875rem;
  height: 0.875rem;
  flex-shrink: 0;
`

const AnnotationMetadataSection = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
`

const MeasurementsSection = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`

const MeasurementItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`

const MeasurementLabel = styled.span`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05rem;
`

const MeasurementValue = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  white-space: nowrap;
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: stretch;
`

const ActionButton = styled.button<{
  $variant?: 'ok' | 'skip' | 'reject'
  $isPressed?: boolean
}>`
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid
    ${({ $variant }) => {
      if ($variant === 'ok') return '#4caf50'
      if ($variant === 'reject') return '#f44336'
      return 'rgba(255, 255, 255, 0.3)'
    }};
  background: ${({ $variant }) => {
    if ($variant === 'ok') return 'rgba(76, 175, 80, 0.2)'
    if ($variant === 'reject') return 'rgba(244, 67, 54, 0.2)'
    return 'rgba(0, 0, 0, 0.5)'
  }};
  color: white;
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.1s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 70px;
  transform: ${({ $isPressed }) => ($isPressed ? 'scale(0.95)' : 'scale(1)')};

  &:hover {
    background: ${({ $variant }) => {
      if ($variant === 'ok') return 'rgba(76, 175, 80, 0.3)'
      if ($variant === 'reject') return 'rgba(244, 67, 54, 0.3)'
      return 'rgba(255, 255, 255, 0.1)'
    }};
    border-color: ${({ $variant }) => {
      if ($variant === 'ok') return '#66bb6a'
      if ($variant === 'reject') return '#ef5350'
      return 'rgba(255, 255, 255, 0.5)'
    }};
  }

  &:active {
    transform: scale(0.95);
  }
`

const KeyboardKey = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.2rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.2rem;
  font-size: 0.6rem;
  font-family: monospace;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`

const SoundToggleButton = styled.div<{ $enabled: boolean }>`
  cursor: pointer;
  color: ${({ $enabled }) =>
    $enabled ? 'rgba(34, 197, 94, 0.9)' : 'rgba(156, 163, 175, 0.6)'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  position: relative;
  filter: ${({ $enabled }) => ($enabled ? 'none' : 'grayscale(100%)')};

  &::after {
    content: '';
    position: absolute;
    width: ${({ $enabled }) => ($enabled ? '0' : '150%')};
    height: 2px;
    background: rgba(156, 163, 175, 0.8);
    transform: rotate(-45deg);
    transition: width 0.3s ease;
  }

  &:hover {
    transform: scale(1.15);
    color: ${({ $enabled }) =>
      $enabled ? 'rgba(34, 197, 94, 1)' : 'rgba(156, 163, 175, 0.6)'};
    filter: ${({ $enabled }) =>
      $enabled ? 'drop-shadow(0 0 8px rgba(34, 197, 94, 0.6))' : 'none'};

    &::after {
      width: ${({ $enabled }) => ($enabled ? '0' : '150%')};
      background: ${({ $enabled }) =>
        $enabled ? 'transparent' : 'rgba(156, 163, 175, 0.6)'};
    }
  }

  &:active {
    transform: scale(1.05);
  }
`

const CloseButtonContainer = styled.div`
  cursor: pointer;
  color: white;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`

const NoAnnotationsMessage = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  text-align: center;
  padding: 2rem;
`

export const DataValidationPanel: React.FC<{
  onClose: () => void
}> = ({ onClose }) => {
  const [isClosing, setIsClosing] = useState(false)
  const {
    kestrelModel,
    sendKVCommand,
    annotations,
    setAnnotations,
    selectedMarkup,
    setSelectedMarkup,
    annotationSortKey,
  } = useKestrel()
  const { currentUnit } = useKestrelViewerContext()
  const { zoomToAnnotation } = useZoomToAnnotation()
  const { rolodexPatch } = useRolodexEdit()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [severity, setSeverity] = useState<string>('')
  const [skipPressed, setSkipPressed] = useState(false)
  const [rejectPressed, setRejectPressed] = useState(false)
  const [okPressed, setOkPressed] = useState(false)
  const [annotationFilter, setAnnotationFilter] = useState<string>('all')
  const { locale } = useGeckoLocale()

  const { playSuccessSound } = useSuccessSound(soundEnabled)
  const { playDeleteSound } = useDeleteSound(soundEnabled)

  const [currentLabel, setCurrentLabel] = useState('')
  const [currentNotes, setCurrentNotes] = useState('')
  const [currentDamageTypes, setCurrentDamageTypes] = useState<
    RooftopDamageId[]
  >([])

  const inspectionAreaIdToLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    kestrelModel?.data?.inspectionAreas?.forEach((area) => {
      map.set(area.id, area.label)
    })
    return map
  }, [kestrelModel?.data?.inspectionAreas])

  const {
    updateLabel,
    updateSeverity,
    updateNotes,
    updateDamageTypes,
    flushPendingSave,
  } = useAnnotationAutoSave({
    kestrelModelId: kestrelModel?.id,
    annotations,
    setAnnotations,
  })

  // Track whether the annotation list has been exhausted (reached the end at least once)
  const hasExhaustedListRef = useRef(false)

  // Track time spent in data validation panel for session metrics
  useEffect(() => {
    recordDataValidationStart()
    return () => {
      recordDataValidationEnd()
    }
  }, [])

  const inspectionAreaValues = useMemo(() => {
    if (!annotations) return []

    const values = new Set<string>()
    annotations.forEach((annotation) => {
      if (
        annotation.tags &&
        annotation.tags[annotationSortKey] &&
        annotation.tags[annotationSortKey].value
      ) {
        values.add(annotation.tags[annotationSortKey].value)
      }
    })

    return Array.from(values).sort()
  }, [annotations, annotationSortKey])

  const rooftopAnnotations = useMemo(() => {
    if (!annotations) return []

    switch (annotationFilter) {
      case 'all':
        return annotations
      case 'validated':
        return annotations.filter((annotation) => annotation.humanValidated)
      case 'unvalidated':
        return annotations.filter((annotation) => !annotation.humanValidated)
      default:
        return annotations.filter(
          (annotation) =>
            annotation.tags?.[annotationSortKey]?.value === annotationFilter
        )
    }
  }, [annotations, annotationFilter, annotationSortKey])

  const currentAnnotation = rooftopAnnotations[currentIndex]

  // Stringify for stable dependency comparison
  const currentDamageTypesJson = JSON.stringify(
    currentAnnotation?.rooftopAnnotation?.selected
  )

  // Sync local state with current annotation
  useEffect(() => {
    setCurrentLabel(currentAnnotation?.label ?? '')
    setSeverity(currentAnnotation?.severity ?? '')
    setCurrentNotes(currentAnnotation?.notes ?? '')
    setCurrentDamageTypes(currentAnnotation?.rooftopAnnotation?.selected ?? [])
  }, [
    currentAnnotation?.id,
    currentAnnotation?.label,
    currentAnnotation?.severity,
    currentAnnotation?.notes,
    currentAnnotation?.rooftopAnnotation?.selected,
    currentDamageTypesJson,
  ])

  const handleLabelChange = useCallback(
    (annotationId: string, label: string) => {
      setCurrentLabel(label)
      updateLabel(annotationId, label)
    },
    [updateLabel]
  )

  const handleNotesChange = useCallback(
    (annotationId: string, notes: string) => {
      setCurrentNotes(notes)
      updateNotes(annotationId, notes)
    },
    [updateNotes]
  )

  const handleDamageTypesChange = useCallback(
    (annotationId: string, damageTypes: RooftopDamageId[]) => {
      setCurrentDamageTypes(damageTypes)
      updateDamageTypes(annotationId, damageTypes)
    },
    [updateDamageTypes]
  )

  const handleSeverityChange = useCallback(
    (newSeverity: string) => {
      setSeverity(newSeverity)
      if (currentAnnotation?.id) {
        updateSeverity(currentAnnotation.id, newSeverity)
      }
    },
    [currentAnnotation?.id, updateSeverity]
  )

  const handleClose = () => {
    flushPendingSave()
    setIsClosing(true)
    setTimeout(() => {
      onClose()
    }, 250)
  }

  const moveToNext = useCallback(() => {
    flushPendingSave()
    setCurrentIndex((prev) => {
      // Check if we're at the last annotation (list exhaustion)
      if (prev >= rooftopAnnotations.length - 1) {
        // Record list exhaustion for session metrics (only fires once per session)
        if (!hasExhaustedListRef.current) {
          hasExhaustedListRef.current = true
          recordDataValidationListExhausted(rooftopAnnotations.length)
        }
        // Loop back to start
        return 0
      }
      return prev + 1
    })
  }, [rooftopAnnotations.length, flushPendingSave])

  const handleOk = useCallback(() => {
    const currentAnnotation = rooftopAnnotations[currentIndex]
    if (!currentAnnotation || !annotations) return

    playSuccessSound()
    setOkPressed(true)
    setTimeout(() => setOkPressed(false), 150)

    const updatedAnnotations = annotations.map((a) =>
      a.id === currentAnnotation.id
        ? { ...a, humanValidated: true, severity: severity || a.severity }
        : a
    )

    setAnnotations(updatedAnnotations)

    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel?.id ?? '',
      data: {
        annotations: updatedAnnotations.map((a) => KVAnnotation.toJSON(a)),
      },
    }
    rolodexPatch([patchData])

    moveToNext()
  }, [
    rooftopAnnotations,
    currentIndex,
    annotations,
    playSuccessSound,
    severity,
    setAnnotations,
    kestrelModel,
    rolodexPatch,
    moveToNext,
  ])

  const handleSkip = useCallback(() => {
    // TODO: Skip without saving
    setSkipPressed(true)
    setTimeout(() => setSkipPressed(false), 150)
    moveToNext()
  }, [moveToNext])

  const handleReject = useCallback(() => {
    const currentAnnotation = rooftopAnnotations[currentIndex]
    if (!currentAnnotation || !annotations) return

    playDeleteSound()
    setRejectPressed(true)
    setTimeout(() => setRejectPressed(false), 150)

    const filteredAnnotations = annotations.filter(
      (a) => a.id !== currentAnnotation.id
    )

    const newAnnotationCount = filteredAnnotations.length

    setAnnotations(filteredAnnotations)

    if (sendKVCommand) {
      if (currentAnnotation.geometry?.kind === 'point') {
        sendKVCommand({
          command: KestrelCommands.DELETE_ANNOTATION,
          annotationId: currentAnnotation.id,
        })
      } else if (currentAnnotation.geometry?.kind === 'polygon') {
        sendKVCommand({
          command: KestrelCommands.DELETE_SURFACE_AREA,
          surfaceAreaId: currentAnnotation.id,
        })
      }
    }

    if (selectedMarkup?.id === currentAnnotation.id) {
      setSelectedMarkup(undefined)
    }

    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel?.id ?? '',
      data: {
        annotations:
          filteredAnnotations?.map((a) => KVAnnotation.toJSON(a)) ?? [],
      },
    }
    rolodexPatch([patchData])

    // Adjust index based on the new count
    // If we deleted the last item, move back one index
    // Otherwise stay at same index (next item shifts into this position)
    if (newAnnotationCount === 0) {
      setCurrentIndex(0)
    } else if (currentIndex >= newAnnotationCount) {
      setCurrentIndex(newAnnotationCount - 1)
    }
    // If currentIndex < newAnnotationCount, stay at current index - next item appears
  }, [
    rooftopAnnotations,
    currentIndex,
    annotations,
    setAnnotations,
    sendKVCommand,
    selectedMarkup,
    setSelectedMarkup,
    kestrelModel,
    rolodexPatch,
    playDeleteSound,
  ])

  const handleAnnotationLoad = useCallback(
    (annotation: KVAnnotation) => {
      zoomToAnnotation(annotation)
    },
    [zoomToAnnotation]
  )

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      if (event.key === ']') {
        event.preventDefault()
        handleOk()
      } else if (event.key === '[') {
        event.preventDefault()
        handleSkip()
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault()
        handleReject()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleOk, handleSkip, handleReject])

  if (rooftopAnnotations.length === 0) {
    return (
      <PanelContainer $isClosing={isClosing}>
        <PanelHeader>
          <PanelHeaderLeft>
            <FilterDropdown
              value={annotationFilter}
              onChange={(value) => {
                setAnnotationFilter(value as string)
                setCurrentIndex(0)
              }}
              size='small'
              popupMatchSelectWidth={false}>
              <Select.Option value='all'>
                All ({annotations?.length ?? 0})
              </Select.Option>
              <Select.Option value='validated'>
                Validated (
                {annotations?.filter((annotation) => annotation.humanValidated)
                  .length ?? 0}
                )
              </Select.Option>
              <Select.Option value='unvalidated'>
                Unvalidated (
                {annotations?.filter((annotation) => !annotation.humanValidated)
                  .length ?? 0}
                )
              </Select.Option>
              {inspectionAreaValues.map((value) => (
                <Select.Option key={value} value={value}>
                  {value} (
                  {annotations?.filter(
                    (annotation) =>
                      annotation.tags?.[annotationSortKey]?.value === value
                  ).length ?? 0}
                  )
                </Select.Option>
              ))}
            </FilterDropdown>
            <PanelHeaderRow>
              <PanelTitle>DATA VALIDATION</PanelTitle>
            </PanelHeaderRow>
          </PanelHeaderLeft>
          <HeaderActions>
            <CloseButtonContainer onClick={handleClose}>
              <CloseCircleOutlined style={{ fontSize: '1.25rem' }} />
            </CloseButtonContainer>
          </HeaderActions>
        </PanelHeader>
        <NoAnnotationsMessage>No annotations to validate</NoAnnotationsMessage>
      </PanelContainer>
    )
  }

  return (
    <PanelContainer $isClosing={isClosing}>
      <PanelHeader>
        <PanelHeaderLeft>
          <PanelHeaderTopRow>
            <FilterDropdown
              value={annotationFilter}
              onChange={(value) => {
                setAnnotationFilter(value as string)
                setCurrentIndex(0)
              }}
              size='small'
              popupMatchSelectWidth={false}>
              <Select.Option value='all'>
                All ({annotations?.length ?? 0})
              </Select.Option>
              <Select.Option value='validated'>
                Validated (
                {annotations?.filter((annotation) => annotation.humanValidated)
                  .length ?? 0}
                )
              </Select.Option>
              <Select.Option value='unvalidated'>
                Unvalidated (
                {annotations?.filter((annotation) => !annotation.humanValidated)
                  .length ?? 0}
                )
              </Select.Option>
              {inspectionAreaValues.map((value) => (
                <Select.Option key={value} value={value}>
                  {inspectionAreaIdToLabelMap.get(value) || value} (
                  {annotations?.filter(
                    (annotation) =>
                      annotation.tags?.[annotationSortKey]?.value === value
                  ).length ?? 0}
                  )
                </Select.Option>
              ))}
            </FilterDropdown>
            <PanelTitle>
              <AnnotationCounter>
                {currentIndex + 1} / {rooftopAnnotations.length}
              </AnnotationCounter>
            </PanelTitle>
            <MeasurementsSection>
              {currentAnnotation?.geometry?.kind === 'polygon' &&
              currentAnnotation.area !== undefined &&
              currentAnnotation.perimeter !== undefined ? (
                <>
                  <MeasurementItem>
                    <MeasurementLabel>Area:</MeasurementLabel>
                    <MeasurementValue>
                      {formatArea(currentAnnotation.area, currentUnit, locale)}
                    </MeasurementValue>
                  </MeasurementItem>
                  <MeasurementItem>
                    <MeasurementLabel>Perimeter:</MeasurementLabel>
                    <MeasurementValue>
                      {formatPerimeter(
                        currentAnnotation.perimeter,
                        currentUnit,
                        locale
                      )}
                    </MeasurementValue>
                  </MeasurementItem>
                </>
              ) : null}
            </MeasurementsSection>
          </PanelHeaderTopRow>
          <PanelHeaderRow>
            <AnnotationLabelContainer>
              {currentAnnotation?.humanValidated && <ValidatedIcon />}
              <AnnotationLabelInput
                value={currentLabel}
                onChange={(e) =>
                  handleLabelChange(currentAnnotation?.id ?? '', e.target.value)
                }
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                placeholder='Enter label...'
              />
            </AnnotationLabelContainer>
            <AnnotationMetadataSection>
              <SeverityPicker
                possibleValues={['1', '2', '3', '4', '5']}
                currentSeverity={severity}
                setSeverity={handleSeverityChange}
              />
            </AnnotationMetadataSection>
          </PanelHeaderRow>
        </PanelHeaderLeft>
        <HeaderActions>
          <ActionButtons>
            <ActionButton onClick={handleSkip} $isPressed={skipPressed}>
              Skip
              <KeyboardKey>[</KeyboardKey>
            </ActionButton>
            <ActionButton
              $variant='reject'
              onClick={handleReject}
              $isPressed={rejectPressed}>
              Delete
              <KeyboardKey>⌫</KeyboardKey>
            </ActionButton>
            <ActionButton
              $variant='ok'
              onClick={handleOk}
              $isPressed={okPressed}>
              OK
              <KeyboardKey>]</KeyboardKey>
            </ActionButton>
          </ActionButtons>
          <SoundToggleButton
            $enabled={soundEnabled}
            onClick={() => setSoundEnabled(!soundEnabled)}>
            {soundEnabled ? (
              <SoundFilled style={{ fontSize: '1.25rem' }} />
            ) : (
              <SoundOutlined style={{ fontSize: '1.25rem' }} />
            )}
          </SoundToggleButton>
          <CloseButtonContainer onClick={handleClose}>
            <CloseCircleOutlined style={{ fontSize: '1.25rem' }} />
          </CloseButtonContainer>
        </HeaderActions>
      </PanelHeader>
      <DataValidationPanelContent
        annotations={rooftopAnnotations}
        currentIndex={currentIndex}
        onAnnotationLoad={handleAnnotationLoad}
        kestrelModelId={kestrelModel?.id}
        currentUnit={currentUnit}
        soundEnabled={soundEnabled}
        severity={severity}
        onSeverityChange={setSeverity}
        onNotesChange={handleNotesChange}
        currentNotes={currentNotes}
        onDamageTypesChange={handleDamageTypesChange}
        currentDamageTypes={currentDamageTypes}
      />
    </PanelContainer>
  )
}
